import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import moment from "moment/moment";
import { fetchCustomerStats } from "../../api/apiJetpack";
import KC_Heading2 from "../../../../designsystem/typography/KC-Heading/KC_Heading2";
import KC_Table from "../../../../designsystem/KC_Table/KC_Table";
import TSUtils from "../../../../utils/helpers/TSUtils";
import { tt } from "../../../../utils/i18n/i18n";
import KC_Separator from "../../../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../../../designsystem/ds_commons";
import KC_Text from "../../../../designsystem/typography/KC_Text/KC_Text";
import { DEFAULT_STALE_CACHE_TIME } from "../../../../utils/constants";
import {
  DEFAULT_CURRENCY_SUB_UNITS,
  DEFAULT_CURRENCY
} from "@tootsweet/model/enums/Currencies";
import { setLoading } from "../../../../redux/actions/app";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import MonthlyStatsDetailRow from "./MonthlyStatsDetailRow";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";
import { MONTHLY_STATS } from "../../../../utils/routes/routes";
import TwemojiWrapper from "../../../../components/TwemojiWrapper/TwemojiWrapper";
import { buildPathWithTrackingId } from "../../../../utils/routing/routingUtils";
import CurrencyProp from "../model/CurrencyProp";
import AvgBasketPromptOverlay from "../AvgBasketPromptOverlay";
import { MonthlyStatsI } from "kadow-jetpack-model/src";
import { recomputeValueEstimationForAction } from "../../helper/recomputeValueEstimationForAction";
import { recomputeEffectiveByAvgBasket } from "../../helper/recomputeEffectiveByAvgBasket";

export default function MonthlyStatsDetail() {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const location = useLocation();
  const history = useHistory();

  const initialMonthYear = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const m = parseInt(params.get("month") || "", 10);
    const y = parseInt(params.get("year") || "", 10);
    if (!isNaN(m) && !isNaN(y)) {
      return { month: m, year: y };
    }
    return { month: moment().month() + 1, year: moment().year() };
  }, []);

  const [selectedMonth, setSelectedMonth] = useState<number>(
    initialMonthYear.month
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    initialMonthYear.year
  );

  const [selectedDateInterval, setSelectedDateInterval] = useState<{
    sts: number;
    ets: number;
  }>(() => {
    const isoMonth = initialMonthYear.month - 1;
    return {
      sts: moment({ year: initialMonthYear.year, month: isoMonth })
        .startOf("month")
        .unix(),
      ets: moment({ year: initialMonthYear.year, month: isoMonth })
        .endOf("month")
        .unix()
    };
  });

  const currency = useMemo<CurrencyProp>(() => {
    return {
      trigram:
        accountToDisplay?.dCurrency ||
        accountToDisplay?.currency ||
        DEFAULT_CURRENCY,
      subUnits: accountToDisplay?.curSubUnits || DEFAULT_CURRENCY_SUB_UNITS
    };
  }, [accountToDisplay]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const month = params.get("month");
    const year = params.get("year");

    if (month && year) {
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);

      if (!isNaN(m) && !isNaN(y)) {
        setSelectedMonth(m);
        setSelectedYear(y);
        const isoMonth = m - 1;
        const start = moment({ year: y, month: isoMonth })
          .startOf("month")
          .unix();
        const end = moment({ year: y, month: isoMonth }).endOf("month").unix();
        setSelectedDateInterval({ sts: start, ets: end });
      }
    }
  }, [location.search]);

  useEffect(() => {
    const start = moment({ year: selectedYear, month: selectedMonth - 1 })
      .startOf("month")
      .unix();

    const end = moment({ year: selectedYear, month: selectedMonth - 1 })
      .endOf("month")
      .unix();

    setSelectedDateInterval({ sts: start, ets: end });
  }, [selectedMonth, selectedYear]);

  const monthText = useMemo(() => {
    return moment
      .unix(selectedDateInterval.sts)
      .format(tt("date_format_month_year"));
  }, [selectedDateInterval]);

  const { data, isLoading, isFetching, isRefetching, error } = useQuery({
    queryKey: [
      "customerStats",
      accountToDisplay?.trackingID,
      selectedDateInterval.sts,
      selectedDateInterval.ets
    ],
    queryFn: () =>
      fetchCustomerStats(
        accountToDisplay?.trackingID,
        selectedDateInterval.sts,
        selectedDateInterval.ets,
        accountToDisplay?.avgBasket,
        accountToDisplay?.bizContrib,
        currency.subUnits
      ),
    enabled:
      !!accountToDisplay?.trackingID &&
      !!accountToDisplay?.avgBasket &&
      !!accountToDisplay?.bizContrib,
    staleTime: DEFAULT_STALE_CACHE_TIME
  });

  const totalBoostValue = useMemo(() => {
    return data?.totalBoostValue || 0;
  }, [data]);

  const stats = useMemo<MonthlyStatsI | null>(() => {
    if (!data?.boostingValue) {
      return null;
    }
    return data.boostingValue;
  }, [data]);

  useEffect(() => {
    dispatch(setLoading(isLoading || isFetching || isRefetching));
  }, [isLoading, isFetching, isRefetching]);

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  if (!accountToDisplay) {
    return null;
  }

  return (
    <div className="dashboard-v2 px-4 position-relative">
      <AvgBasketPromptOverlay
        accountToDisplay={accountToDisplay}
        currency={currency}
        messageKey="analytics_no_avg_basket_message_monthly"
      >
        <KC_Separator size={KC_Sizes.l} />
        <div className="d-flex flex-row">
          <div>
            <KC_Button
              className="d-flex kc-btn-border-black-light"
              size={KC_ButtonSize.Large}
              isInverted={true}
              text={tt("monthly_stats_back_button")}
              onClick={() => {
                history.push(
                  buildPathWithTrackingId(
                    MONTHLY_STATS,
                    accountToDisplay?.trackingID
                  )
                );
              }}
            />
          </div>
        </div>
        <KC_Separator size={KC_Sizes.m} />
        <TwemojiWrapper isBigText={true}>
          <KC_Heading2
            className="text-center"
            text={tt("monthly_stats_boost_message", {
              monthText,
              amount: TSUtils.formatAmountUsingIntl(
                totalBoostValue,
                currency.subUnits,
                currency.trigram
              )
            })}
          />
        </TwemojiWrapper>
        <KC_Separator size={KC_Sizes.m} />
        {!isLoading && totalBoostValue === 0 ? (
          <div className="text-center">
            <KC_Text
              size="md"
              weight="reg"
              text={tt("monthly_stats_no_communication_materials")}
            />
          </div>
        ) : (
          <KC_Table
            keyPrefix={"monthlyStats"}
            border={false}
            headers={
              <>
                <td>
                  <KC_Text
                    size={"md"}
                    weight={"bold"}
                    text={tt("monthly_stats_results_header", { monthText })}
                  />
                </td>
                <td>
                  <KC_Text
                    size={"md"}
                    weight={"bold"}
                    text={tt("monthly_stats_estimated_value_header", {
                      currency: currency.trigram
                    })}
                  />
                </td>
                <td>
                  <KC_Text
                    size={"md"}
                    weight={"bold"}
                    text={tt("monthly_stats_estimation_method_header")}
                  />
                </td>
              </>
            }
            children={
              <>
                <MonthlyStatsDetailRow
                  emoji="⭐"
                  amount={stats?.nCTAGoogle || 0}
                  text={tt("monthly_stats_google_actions")}
                  computeFn={recomputeValueEstimationForAction}
                  multiplier={1}
                  hideIfZero={true}
                  subText={tt("monthly_stats_per_action", {
                    amount: TSUtils.formatAmountUsingIntl(
                      accountToDisplay.bizContrib,
                      currency.subUnits,
                      currency.trigram
                    )
                  })}
                  currency={currency}
                  ctaValue={accountToDisplay.bizContrib}
                />
                <MonthlyStatsDetailRow
                  emoji="📱"
                  amount={stats?.nCTAFacebook || 0}
                  text={tt("monthly_stats_facebook_actions")}
                  computeFn={recomputeValueEstimationForAction}
                  multiplier={1}
                  hideIfZero={true}
                  subText={tt("monthly_stats_per_action", {
                    amount: TSUtils.formatAmountUsingIntl(
                      accountToDisplay.bizContrib,
                      currency.subUnits,
                      currency.trigram
                    )
                  })}
                  currency={currency}
                  ctaValue={accountToDisplay.bizContrib}
                />
                <MonthlyStatsDetailRow
                  emoji="📱"
                  amount={stats?.nCTAInstagram || 0}
                  text={tt("monthly_stats_instagram_actions")}
                  computeFn={recomputeValueEstimationForAction}
                  multiplier={1}
                  hideIfZero={true}
                  subText={tt("monthly_stats_per_action", {
                    amount: TSUtils.formatAmountUsingIntl(
                      accountToDisplay.bizContrib,
                      currency.subUnits,
                      currency.trigram
                    )
                  })}
                  currency={currency}
                  ctaValue={accountToDisplay.bizContrib}
                />
                <MonthlyStatsDetailRow
                  emoji="💌"
                  amount={stats?.nCollectedEmail || 0}
                  text={tt("monthly_stats_emails_collected")}
                  computeFn={recomputeValueEstimationForAction}
                  multiplier={1}
                  hideIfZero={true}
                  subText={tt("monthly_stats_per_email", {
                    amount: TSUtils.formatAmountUsingIntl(
                      accountToDisplay.bizContrib,
                      currency.subUnits,
                      currency.trigram
                    )
                  })}
                  currency={currency}
                  ctaValue={accountToDisplay.bizContrib}
                />
                <MonthlyStatsDetailRow
                  emoji="📞"
                  amount={stats?.nCollectedPhone || 0}
                  text={tt("monthly_stats_phones_collected")}
                  computeFn={recomputeValueEstimationForAction}
                  multiplier={1}
                  hideIfZero={true}
                  subText={tt("monthly_stats_per_number", {
                    amount: TSUtils.formatAmountUsingIntl(
                      accountToDisplay.bizContrib,
                      currency.subUnits,
                      currency.trigram
                    )
                  })}
                  currency={currency}
                  ctaValue={accountToDisplay.bizContrib}
                />
                <MonthlyStatsDetailRow
                  emoji="📞"
                  amount={stats?.nCollectedBirthday || 0}
                  text={tt("monthly_stats_birthdays_collected")}
                  computeFn={recomputeValueEstimationForAction}
                  multiplier={0.2}
                  hideIfZero={true}
                  subText={tt("monthly_stats_per_birthday", {
                    amount: TSUtils.formatAmountUsingIntl(
                      accountToDisplay.bizContrib * 0.2,
                      currency.subUnits,
                      currency.trigram
                    )
                  })}
                  currency={currency}
                  ctaValue={accountToDisplay.bizContrib}
                />
                <MonthlyStatsDetailRow
                  emoji="✅"
                  amount={stats?.nCTACheckin || 0}
                  text={tt("monthly_stats_check_in")}
                  computeFn={recomputeValueEstimationForAction}
                  multiplier={stats?.avgBasketMultiplier ?? 1}
                  hideIfZero={true}
                  subText={tt("monthly_stats_per_checkin", {
                    amount: TSUtils.formatAmountUsingIntl(
                      accountToDisplay.avgBasket * currency.subUnits,
                      currency.subUnits,
                      currency.trigram
                    )
                  })}
                  currency={currency}
                  ctaValue={accountToDisplay.avgBasket}
                />
                <MonthlyStatsDetailRow
                  emoji="📸"
                  amount={stats?.sumOfReceipts || 0}
                  isAmount={true}
                  text={tt("monthly_stats_receipts_scanned")}
                  computeFn={(a, b) => {
                    return TSUtils.formatAmountUsingIntl(
                      stats?.sumOfReceipts || 0,
                      currency.subUnits,
                      currency.trigram
                    );
                  }}
                  multiplier={1}
                  hideIfZero={true}
                  subText={tt("monthly_stats_receipts_scanned_amounts")}
                  currency={currency}
                  ctaValue={accountToDisplay.bizContrib}
                />
                {!!stats?.nRedeem && (
                  <tr>
                    <td>
                      🎁 {stats?.nRedeem}{" "}
                      {!!stats?.nRedeem && stats?.nRedeem > 1
                        ? tt("monthly_stats_gifts_redeemed_plural")
                        : tt("monthly_stats_gifts_redeemed_singular")}
                    </td>
                    <td className="text-center">
                      {recomputeEffectiveByAvgBasket(
                        stats?.nRedeem,
                        accountToDisplay.avgBasket,
                        stats?.avgBasketMultiplier,
                        currency.trigram,
                        currency.subUnits
                      )}
                    </td>
                    <td>
                      {tt("monthly_stats_new_visits_calculation", {
                        avgBasket: TSUtils.formatAmountUsingIntl(
                          accountToDisplay.avgBasket * currency.subUnits,
                          currency.subUnits,
                          currency.trigram
                        ),
                        multiplier: stats?.avgBasketMultiplier
                      })}
                    </td>
                  </tr>
                )}

                {!!stats?.nAuthenticatedSponsoree && (
                  <tr>
                    <td>
                      📣 {stats?.nAuthenticatedSponsoree || 0}{" "}
                      {tt(
                        (stats?.nAuthenticatedSponsoree || 0) > 1
                          ? "monthly_stats_sponsored_authentications_plural"
                          : "monthly_stats_sponsored_authentications_singular",
                        {
                          currency: currency.trigram
                        }
                      )}
                    </td>
                    <td className="text-center">
                      {TSUtils.formatAmountUsingIntl(
                        (stats?.nAuthenticatedSponsoree || 0) *
                          2 *
                          accountToDisplay.bizContrib,
                        currency.subUnits,
                        currency.trigram
                      )}
                    </td>
                    <td>
                      {tt("monthly_stats_per_sponsorship", {
                        amount: TSUtils.formatAmountUsingIntl(
                          2 * accountToDisplay.bizContrib,
                          currency.subUnits,
                          currency.trigram
                        )
                      })}
                    </td>
                  </tr>
                )}

                {!!stats?.nSponsoredCus && (
                  <tr>
                    <td>
                      🫂 {stats?.nSponsoredCus}{" "}
                      {stats?.nSponsoredCus > 1
                        ? tt("monthly_stats_new_sponsored_customers_plural")
                        : tt("monthly_stats_new_sponsored_customers_singular")}
                    </td>
                    <td className="text-center">
                      {recomputeEffectiveByAvgBasket(
                        stats?.nSponsoredCus,
                        accountToDisplay.avgBasket,
                        stats?.avgBasketMultiplier || 1.5,
                        currency.trigram,
                        currency.subUnits
                      )}
                    </td>
                    <td>
                      {tt("monthly_stats_new_visits_calculation", {
                        avgBasket: TSUtils.formatAmountUsingIntl(
                          accountToDisplay.avgBasket * currency.subUnits,
                          currency.subUnits,
                          currency.trigram
                        ),
                        multiplier: stats?.avgBasketMultiplier
                      })}
                    </td>
                  </tr>
                )}

                <tr>
                  <td>
                    <KC_Text
                      size="md"
                      weight="bold"
                      text={tt("monthly_stats_total")}
                    />
                  </td>
                  <td className="text-center">
                    <KC_Text
                      size="md"
                      weight="bold"
                      text={TSUtils.formatAmountUsingIntl(
                        totalBoostValue,
                        currency.subUnits,
                        currency.trigram
                      )}
                    />
                  </td>
                </tr>
              </>
            }
          />
        )}
      </AvgBasketPromptOverlay>
    </div>
  );
}
